import { ArrowRight } from "lucide-react";
import { useState } from "react";
import type { RecordingInsight } from "@/lib/api";
import { categoryMeta } from "@/lib/insightCategory";
import { ApproveSourceBar } from "@/components/ApproveSourceBar";

/**
 * One source's insights in the order they were read out of it, each with its review state — the
 * recording detail's Insights tab (UI-5) and the note and document page's.
 *
 * Every state is shown: the source's own page is one of the two places a pending or discarded
 * insight is legibly visible (with the review queue). Beneath the whole list, INS-7's bar approves
 * what is still pending — drawn after the rows, sending the ids of the rows drawn.
 */
export function SourceInsightList({
  sourceId,
  sourceName,
  insights,
  onViewInSource,
  onApproved,
  onStale,
}: {
  sourceId: string;
  sourceName: string | null;
  insights: RecordingInsight[];
  /** UI-7's jump to the lines an insight was drawn from, where the page has a transcript. */
  onViewInSource?: (insightId: string) => void;
  onApproved: (count: number) => void;
  onStale: () => void;
}) {
  const pending = insights.filter((insight) => insight.reviewState === "pending");
  // INS-7 — a refused approve-all, said above the list the owner is asked to read again. The
  // caller reloads the rows in place, so this component stays mounted and the sentence stays.
  const [stale, setStale] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      {stale && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {stale}
        </p>
      )}

      <ul className="flex flex-col gap-[var(--space-3)]">
        {insights.map((insight) => {
          const meta = categoryMeta(insight.category);

          return (
            <li
              key={insight.id}
              className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] backdrop-blur-[var(--blur-sm)]"
            >
              <div className="flex flex-wrap items-center gap-[var(--space-2)]">
                <span
                  className="rounded-[var(--radius-full)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)]"
                  style={{ background: `color-mix(in oklch, ${meta.colour} 18%, transparent)`, color: meta.colour }}
                >
                  {meta.label}
                </span>
                <ReviewStateChip state={insight.reviewState} />
              </div>

              <p className="text-[length:var(--text-md)] leading-relaxed text-[var(--text-primary)]">
                {insight.normalizedText}
              </p>

              {insight.entities.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {insight.entities.map((entity) => (
                    <span
                      key={entity.id}
                      className="rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-2 py-0.5 text-[length:var(--text-2xs)] font-medium text-[var(--text-secondary)]"
                    >
                      {entity.name}
                    </span>
                  ))}
                </div>
              )}

              {/* UI-7 — jump to the transcript with the lines this insight was drawn from lit. */}
              {onViewInSource && (
                <button
                  type="button"
                  onClick={() => onViewInSource(insight.id)}
                  className="mt-[var(--space-1)] inline-flex w-fit items-center gap-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
                >
                  View in source
                  <ArrowRight size={12} strokeWidth={2} />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <ApproveSourceBar
        sourceId={sourceId}
        sourceName={sourceName}
        pendingIds={pending.map((insight) => insight.id)}
        onApproved={(count) => {
          setStale(null);
          onApproved(count);
        }}
        onStale={(message) => {
          setStale(message);
          onStale();
        }}
        reviewLink
      />
    </div>
  );
}

/** The insight's place in the review gate: pending until decided, then approved or discarded. */
function ReviewStateChip({ state }: { state: "pending" | "approved" | "discarded" }) {
  const tone =
    state === "approved"
      ? { label: "Approved", colour: "var(--vl-life-soft)" }
      : state === "discarded"
        ? { label: "Discarded", colour: "var(--text-muted)" }
        : { label: "Pending review", colour: "var(--vl-accent-warm)" };

  return (
    <span
      className="rounded-[var(--radius-full)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold"
      style={{ background: "var(--surface-2)", color: tone.colour }}
    >
      {tone.label}
    </span>
  );
}
